import { useSyncExternalStore } from 'react'
import {
  broadcastSessionTimerPauseUpdated,
  subscribeSessionTimerPauseUpdated,
} from './sessionCrossTab'

export interface SessionWallClockPauseRecord {
  sessionLoginAtMs: number
  pauseStartedAtMs: number | null
  accumulatedPauseMs: number
}

const STORAGE_SUFFIX = '_ecrm_session_wall_clock_pause_v1'

let storagePrefix = ''
let revision = 0
const listeners = new Set<() => void>()
let crossTabUnsub: (() => void) | null = null
let storageListenerAttached = false

function emit(): void {
  revision += 1
  for (const l of listeners) {
    l()
  }
}

function getStorageKey(): string | null {
  return storagePrefix ? `${storagePrefix}${STORAGE_SUFFIX}` : null
}

function safeBroadcastPauseUpdated(): void {
  try {
    broadcastSessionTimerPauseUpdated()
  } catch (err) {
    console.error(err)
  }
}

function readRecordRaw(): string | null {
  const key = getStorageKey()
  if (!key) return null
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function parseRecord(raw: string | null): SessionWallClockPauseRecord | null {
  if (!raw) return null
  try {
    const o = JSON.parse(raw) as SessionWallClockPauseRecord
    if (
      typeof o.sessionLoginAtMs !== 'number' ||
      typeof o.accumulatedPauseMs !== 'number' ||
      (o.pauseStartedAtMs !== null && typeof o.pauseStartedAtMs !== 'number')
    ) {
      return null
    }
    return {
      sessionLoginAtMs: o.sessionLoginAtMs,
      pauseStartedAtMs: o.pauseStartedAtMs,
      accumulatedPauseMs: o.accumulatedPauseMs,
    }
  } catch {
    return null
  }
}

export function readSessionWallClockPauseRecord(): SessionWallClockPauseRecord | null {
  return parseRecord(readRecordRaw())
}

function writeRecord(record: SessionWallClockPauseRecord): void {
  const key = getStorageKey()
  if (!key) return
  try {
    sessionStorage.setItem(key, JSON.stringify(record))
  } catch (err) {
    console.error(err)
  }
  emit()
  safeBroadcastPauseUpdated()
}

function ensureListeners(): void {
  if (typeof window === 'undefined') return
  if (!storagePrefix) return

  if (!storageListenerAttached) {
    storageListenerAttached = true
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.storageArea !== sessionStorage) return
      const key = getStorageKey()
      if (!key || e.key !== key) return
      emit()
    })
  }

  if (!crossTabUnsub) {
    try {
      crossTabUnsub = subscribeSessionTimerPauseUpdated(() => {
        emit()
      })
    } catch (err) {
      console.error(err)
    }
  }
}

export function configureSessionTimerPause(prefix: string): void {
  storagePrefix = prefix
  ensureListeners()
}

export function subscribeSessionWallClockPause(
  onStoreChange: () => void
): () => void {
  ensureListeners()
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

export function getSessionWallClockPauseRevision(): number {
  return revision
}

export function getSessionWallClockPauseActive(
  sessionLoginAtMs: number
): boolean {
  if (sessionLoginAtMs <= 0) return false
  const r = readSessionWallClockPauseRecord()
  if (!r || r.sessionLoginAtMs !== sessionLoginAtMs) return false
  return r.pauseStartedAtMs !== null
}

export function getEffectiveSessionElapsedMs(
  sessionLoginAtMs: number,
  nowMs: number = Date.now()
): number {
  if (sessionLoginAtMs <= 0) return 0
  const raw = nowMs - sessionLoginAtMs
  const r = readSessionWallClockPauseRecord()
  if (!r || r.sessionLoginAtMs !== sessionLoginAtMs) {
    return raw
  }
  const ongoing = r.pauseStartedAtMs !== null ? nowMs - r.pauseStartedAtMs : 0
  return raw - r.accumulatedPauseMs - ongoing
}

export function syncSessionTimerPauseForIncomingCall(
  incomingCall: boolean,
  sessionLoginAtMs: number
): void {
  if (!storagePrefix) return
  if (sessionLoginAtMs <= 0) return

  const r = readSessionWallClockPauseRecord()
  if (!r || r.sessionLoginAtMs !== sessionLoginAtMs) {
    writeRecord({
      sessionLoginAtMs,
      pauseStartedAtMs: incomingCall ? Date.now() : null,
      accumulatedPauseMs: 0,
    })
    return
  }

  if (incomingCall) {
    if (r.pauseStartedAtMs === null) {
      writeRecord({ ...r, pauseStartedAtMs: Date.now() })
    }
    return
  }

  if (r.pauseStartedAtMs !== null) {
    const delta = Date.now() - r.pauseStartedAtMs
    writeRecord({
      sessionLoginAtMs,
      pauseStartedAtMs: null,
      accumulatedPauseMs: r.accumulatedPauseMs + delta,
    })
  }
}

export function useSessionWallClockPauseActive(
  sessionLoginAtMs: number
): boolean {
  ensureListeners()
  return useSyncExternalStore(
    subscribeSessionWallClockPause,
    () => getSessionWallClockPauseActive(sessionLoginAtMs),
    () => getSessionWallClockPauseActive(sessionLoginAtMs)
  )
}
